import { Component, OnDestroy, OnInit } from "@angular/core";
import { forkJoin, of, Subscription } from "rxjs";
import { concatMap, map } from "rxjs/operators";
import { catchError } from "rxjs/operators";
import {
  EnterpriseDepartmentUserType,
  ProgressItemResponse,
  SCREENING_INFO_REQUEST_DEADLINE_OPTIONS,
  StudentDetail,
  MailTemplateResponse,
  MailReplacedTemplateRequest,
  StudentMailBulkSendRequest,
  ProgressBulkSettingInterviewRequest,
  Job,
  User,
  ProgressScreeningRequestStatus
} from "@agent-ds/shared/interfaces";
import {
  AuthService,
  DialogService,
  JobApiService,
  MailApiService,
  ProgressApiService,
  StudentApiService,
  UserApiService,
} from "@agent-ds/shared/services";
import { ADJUST_INTERVIEW_SYSTEM_NAMES } from "@agent-ds/shared/constants/mail";
import {
  ProgressScreeningRequestStatusRequestNoEnum,
  ProgressScreeningRequestStatusRequestStatusEnum,
  ProgressStatusCode,
} from "@agent-ds/shared/enums";
import {
  getSeminarTypeLabel,
} from "@agent-ds/shared/constants";
import { ConfirmDialogComponent } from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import { ConfirmDialogConfig } from "../../../shared/components/confirm-dialog/confirm-dialog-config";
import { CacheInterceptor } from "@agent-ds/shared/interceptors/cache.interceptor";
import { getValueFromObject } from "@agent-ds/shared/util/util";
import { DialogConfig } from "../../../shared/components/dialog/dialog-config";
import { DialogRef } from "../../../shared/components/dialog/dialog-ref";
import * as moment from "moment";

@Component({
  selector: "ag-progress-screening-request-notify-dialog",
  templateUrl: "./progress-screening-request-notify-dialog.component.html",
  styleUrls: ["./progress-screening-request-notify-dialog.component.scss"]
})
export class ProgressScreeningRequestNotifyDialogComponent implements OnInit, OnDestroy {
  model: any = {};
  message = "";
  screeningInfo: any = null;
  screeningLabel = "";

  inConfirm = false;
  isTemplateLoadInProgress = false;
  isSendInProgress = false;
  private mailTemplateSubscription: Subscription | null = null;
  private sendSubscription: Subscription | null = null;
  confirmedMailTemplateTypeId: number;

  constructor(
    public dialog: DialogRef,
    public config: DialogConfig,
    private studentApiService: StudentApiService,
    private jobApiService: JobApiService,
    private mailApiService: MailApiService,
    private authService: AuthService,
    private progressApiService: ProgressApiService,
    private userService: UserApiService,
    private dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.load();
  }

  ngOnDestroy() {
    this.cancelMailTemplate();
    this.cancelSend();
  }

  private load(): void {
    const progress: ProgressItemResponse = this.config.data.progress;
    if (!progress) return;

    forkJoin([
      this.studentApiService.getDetail(progress.studentId),
      this.jobApiService.getDetail(progress.jobId),
      this.userService.getAll()
    ]).subscribe(([student, job, users]: [StudentDetail, Job, User[]]) => {

      const raAsUserIds = job.enterpriseDepartmentUsers
        .filter(u => u.type === EnterpriseDepartmentUserType.アシ)
        .map(u => u.userId);
      const raAsUsers = users.filter(u => raAsUserIds.includes(u.id));

      const caUserObj = student.studentUsers.find(u => u.type === EnterpriseDepartmentUserType.CA);
      const caUser = caUserObj ? users.find(u => u.id === caUserObj.userId) : null;

      const emailMain = getValueFromObject(student, "dynamicData.emailMain.email");
      const emailSub = getValueFromObject(student, "dynamicData.emailSub.email");
      const studentName = (student.dynamicData.lastName || "") + (student.dynamicData.firstName || "");

      this.model = {
        to: studentName + " <" + emailMain + ">",
        toEmails: [emailMain],
        ccStudent: emailSub ? studentName + " <" + emailSub + ">" : studentName,
        ccCA: caUser ? caUser.name + " <" + caUser.email + ">" : "",
        ccRA: raAsUsers.length ? raAsUsers.map(u => u.name + " <" + u.email + ">").join(", ") : "",
        cc: [emailSub, caUser ? caUser.email : null, ...raAsUsers.map(u => u.email)].filter(v => v)
      };

      this.setMainScreeningInfo(progress, job);
    });
  }

  private setMainScreeningInfo(progress: ProgressItemResponse, job: Job): void {
    const progressStatus = progress.status;
    const dynamicData = job.dynamicData;

    if ([
      ProgressStatusCode.セミナー設定中,
      ProgressStatusCode.セミナー確認中,
      ProgressStatusCode.セミナー結果待ち,
    ].includes(progressStatus)) {
      this.screeningLabel = 'セミナー';
      this.screeningInfo = dynamicData.screeningInfoSeminar;
    } else if ([
      ProgressStatusCode.面接設定中,
      ProgressStatusCode.面接確認中,
      ProgressStatusCode.面接結果待ち,
    ].includes(progressStatus)) {
      this.screeningLabel = getSeminarTypeLabel('面接', progress.seminarType, progress.n);
      this.screeningInfo = dynamicData["screeningInfo" + progress.n];
    }

    if (this.screeningInfo) {
      [1, 2, 3].forEach(i => this.model["request" + i] = true);
    }
  }

  getMailTemplate(): void {
    if (this.isTemplateLoadInProgress) return;
    this.isTemplateLoadInProgress = true;

    const progressId: number | null =
      this.config.data && this.config.data.progress && this.config.data.progress.id != null
        ? this.config.data.progress.id
        : null;

    this.mailTemplateSubscription = this.mailApiService.getMailTemplates().pipe(
      map((templates: MailTemplateResponse[]) => {
        return templates.find(t => t.systemName === ADJUST_INTERVIEW_SYSTEM_NAMES.STUDENT_SCREENING_REQUEST_NOTIFY);
      }),
      concatMap((template: MailTemplateResponse) => {
        if (!progressId) {
          return of({ template, statuses: [] as ProgressScreeningRequestStatus[] });
        }
        return this.progressApiService.getProgressScreeningRequestStatusList(progressId).pipe(
          catchError(() => of([] as ProgressScreeningRequestStatus[])),
          map((statuses: ProgressScreeningRequestStatus[]) => ({ template, statuses }))
        );
      }),
      concatMap(({ template, statuses }: { template: MailTemplateResponse; statuses: ProgressScreeningRequestStatus[] }) => {
        const templateObj: MailReplacedTemplateRequest = {
          from: this.authService.loginUser.email,
          subject: template.subject,
          text: template.body,
          to: this.model.toEmails,
          jobId: this.config.data.progress.jobId,
          progress: this.config.data.progress,
          additionalReplacements: this.buildAdditionalReplacements(statuses)
        };
        this.confirmedMailTemplateTypeId = template.mailTemplateTypeId;
        return this.mailApiService.replaceTemplate(template.mailTemplateTypeId, templateObj);
      })
    ).subscribe((replaced: Partial<MailTemplateResponse>) => {
      this.model.subject = replaced.subject;
      this.model.body = replaced.body;
      this.inConfirm = true;
      this.isTemplateLoadInProgress = false;
    }, () => this.cancelMailTemplate());
  }

  private buildAdditionalReplacements(
    statuses: ProgressScreeningRequestStatus[]
  ): NonNullable<MailReplacedTemplateRequest["additionalReplacements"]> {
    const correspondence = this.generateCorrespondenceText();
    return [
      {
        label: "対応事項1",
        value: (this.model.request1 && this.screeningInfo.request1) || "",
        withoutAppendingNewline: true,
      },
      {
        label: "対応期限1",
        value: this.buildActionDeadlineReplacement({
          requestNo: ProgressScreeningRequestStatusRequestNoEnum.actionItem1,
          requestEnabled: !!this.model.request1,
          requestText: this.screeningInfo && this.screeningInfo.request1,
          requestType: this.screeningLabel,
          subStatusId: null,
          manualDeadlineDate: this.getManualDeadlineDateDisplay(1),
          correspondence,
          statuses,
        }),
        withoutAppendingNewline: true,
      },
      {
        label: "対応事項2",
        value: (this.model.request2 && this.screeningInfo.request2) || "",
        withoutAppendingNewline: true,
      },
      {
        label: "対応期限2",
        value: this.buildActionDeadlineReplacement({
          requestNo: ProgressScreeningRequestStatusRequestNoEnum.actionItem2,
          requestEnabled: !!this.model.request2,
          requestText: this.screeningInfo && this.screeningInfo.request2,
          requestType: this.screeningLabel,
          subStatusId: null,
          manualDeadlineDate: this.getManualDeadlineDateDisplay(2),
          correspondence,
          statuses,
        }),
        withoutAppendingNewline: true,
      },
      {
        label: "対応事項3",
        value: (this.model.request3 && this.screeningInfo.request3) || "",
        withoutAppendingNewline: true,
      },
      {
        label: "対応期限3",
        value: this.buildActionDeadlineReplacement({
          requestNo: ProgressScreeningRequestStatusRequestNoEnum.actionItem3,
          requestEnabled: !!this.model.request3,
          requestText: this.screeningInfo && this.screeningInfo.request3,
          requestType: this.screeningLabel,
          subStatusId: null,
          manualDeadlineDate: this.getManualDeadlineDateDisplay(3),
          correspondence,
          statuses,
        }),
        withoutAppendingNewline: true,
      },
      { label: "連絡事項", value: (this.message || "").trim() }
    ];
  }

  private buildActionDeadlineReplacement(params: {
    requestNo: ProgressScreeningRequestStatusRequestNoEnum;
    requestEnabled: boolean;
    requestText?: string;
    requestType?: string;
    subStatusId: number | null;
    manualDeadlineDate: string;
    correspondence: string;
    statuses: ProgressScreeningRequestStatus[];
  }): string {
    if (!params.requestEnabled || !params.requestText) {
      return "";
    }

    const statusRecord = this.findLatestRequestStatus(
      params.statuses,
      params.requestNo,
      params.requestType,
      params.subStatusId
    );

    const actionStatus =
      statusRecord && statusRecord.requestStatus === ProgressScreeningRequestStatusRequestStatusEnum.completed
        ? "対応期限：対応済み"
        : "対応期限：未対応";

    const actionLimit = (() => {
      if (params.manualDeadlineDate) {
        return params.manualDeadlineDate;
      }

      if (statusRecord && statusRecord.requestDeadline != null && statusRecord.requestDeadline !== "") {
        const m = moment(statusRecord.requestDeadline).utcOffset(9).locale("ja");
        if (!m.isValid()) {
          return "";
        }
        return `(${m.format("YYYY/MM/DD (ddd)")})`;
      }

      return this.extractDeadlineFromCorrespondence(params.correspondence, params.requestText);
    })();

    return actionLimit ? `${actionStatus} ${actionLimit}` : actionStatus;
  }

  private getManualDeadlineDateDisplay(i: 1 | 2 | 3): string {
    if (!this.screeningInfo) {
      return "";
    }

    const deadlineType = (this.screeningInfo as any)["request" + i + "Deadline"];
    const isManual = deadlineType != null && Number(deadlineType) === 999;
    if (!isManual) {
      return "";
    }

    const selected = this.model && this.model["request" + i + "DeadlineDate"];
    if (!selected) {
      return "";
    }

    const m = moment(selected).utcOffset(9).locale("ja");
    if (!m.isValid()) {
      return "";
    }

    return `(${m.format("YYYY/MM/DD (ddd)")})`;
  }

  private findLatestRequestStatus(
    statuses: ProgressScreeningRequestStatus[],
    requestNo: ProgressScreeningRequestStatusRequestNoEnum,
    requestType?: string,
    subStatusId: number | null = null
  ): ProgressScreeningRequestStatus | null {
    const list = Array.isArray(statuses) ? statuses : [];
    const normalizedType = String(requestType || "").trim();

    const filtered = list.filter((s) => {
      const typeMatch = String(s.requestType || "").trim() === normalizedType;
      const subMatch = subStatusId == null
        ? s.progressSubScreeningStatusId == null
        : Number(s.progressSubScreeningStatusId) === Number(subStatusId);
      const noMatch = Number(s.requestNo) === Number(requestNo);
      return typeMatch && subMatch && noMatch;
    });

    if (!filtered.length) {
      return null;
    }

    return filtered.reduce<ProgressScreeningRequestStatus>((best, cur) => {
      const bestUpdatedAt = (best as any).updatedAt || (best as any).createdAt;
      const curUpdatedAt = (cur as any).updatedAt || (cur as any).createdAt;
      const bestTs = bestUpdatedAt ? new Date(bestUpdatedAt).getTime() : 0;
      const curTs = curUpdatedAt ? new Date(curUpdatedAt).getTime() : 0;

      if (curTs !== bestTs) {
        return curTs > bestTs ? cur : best;
      }

      const bestId = Number(best.id || 0);
      const curId = Number(cur.id || 0);
      return curId > bestId ? cur : best;
    }, filtered[0]);
  }

  private extractDeadlineFromCorrespondence(correspondence: string, requestText: string): string {
    if (!correspondence || !requestText) {
      return "";
    }

    const normalizedRequest = String(requestText).trim();
    const lines = String(correspondence).split(/\r?\n/);

    let lastRequestLine: string | null = null;
    for (const rawLine of lines) {
      const line = String(rawLine).trim();
      if (!line) continue;

      if (line.startsWith("対応期限：")) {
        if (lastRequestLine && lastRequestLine === normalizedRequest) {
          return line.replace(/^対応期限：\s*/, "").trim();
        }
        continue;
      }

      lastRequestLine = line;
    }

    return "";
  }

  private getDeadlineTextForProgressDynamicData(value: any): string {
    if (value === null || value === undefined) {
      return null;
    }

    const numValue = Number(value);
    const option = SCREENING_INFO_REQUEST_DEADLINE_OPTIONS.find(o => o.value === numValue);
    if (!option || option.value === null) {
      return null;
    }

    const isManual = numValue === 999;
    const label = isManual ? "未定" : option.label;
    const subText = isManual ? "今後設定される場合があります。お早めにご対応ください" : "日程確定時に日付が決まります";
    return `対応期限：${label}（${subText}）`;
  }

  private generateCorrespondenceText(): string {
    if (!this.screeningInfo) {
      return '';
    }
    const items = [];
    [1, 2, 3].forEach(i => {
      if (this.model["request" + i] && this.screeningInfo["request" + i]) {
        const content = this.screeningInfo["request" + i];
        const deadline = this.screeningInfo["request" + i + "Deadline"];
        const manual = this.getManualDeadlineDateDisplay(i as 1 | 2 | 3);
        const deadlineLabel = manual
          ? `対応期限：${manual}`
          : this.getDeadlineTextForProgressDynamicData(deadline);
        if (deadlineLabel) {
          items.push(`${content}\n${deadlineLabel}`);
        } else {
          items.push(content);
        }
      }
    });
    return items.join('\n');
  }

  send(): void {
    if (this.isSendInProgress) return;
    this.isSendInProgress = true;

    const screeningRequests = [];
    [1, 2, 3].forEach(i => {
      if (this.screeningInfo && this.screeningInfo['request' + i]) {
        screeningRequests.push({
          requestType: this.screeningLabel,
          requestNo: i,
          isChecked: !!this.model['request' + i],
          manualDeadline: this.model['request' + i + 'DeadlineDate'] || null,
          jobScreeningInfoId: null
        });
      }
    });

    const bulkRequest: ProgressBulkSettingInterviewRequest = {
      progressIds: [this.config.data.progress.id],
      dynamicData: {
        contactFromAdvisor: (this.message || "").trim(),
        correspondence: this.generateCorrespondenceText()
      },
      screeningRequests: screeningRequests
    };

    const bulkMailRequest: StudentMailBulkSendRequest = {
      userId: this.authService.loginUser.id,
      subject: this.model.subject,
      text: this.model.body,
      from: this.authService.loginUser.email,
      to: this.model.toEmails,
      cc: this.model.cc,
      jobId: this.config.data.progress.jobId,
      templateTypeId: this.confirmedMailTemplateTypeId
    };

    this.sendSubscription = this.progressApiService.bulkSettingInterview(bulkRequest).pipe(
      concatMap(() => this.mailApiService.sendStudentBulkMail(bulkMailRequest))
    ).subscribe(() => {
      this.isSendInProgress = false;
      this.close(true);
      this.openSuccessConfirmDialog();
      CacheInterceptor.requestCountChange.emit(0);
    }, () => this.isSendInProgress = false);
  }

  private openSuccessConfirmDialog(): void {
    const config: ConfirmDialogConfig = {
      title: "対応事項案内",
      messages: ["案内メールの送信と記録の保存を完了しました。"],
      style: { height: "200px", width: "400px" },
      buttons: { no: "", hideNo: true, yes: "閉じる" }
    };
    this.dialogService.open(ConfirmDialogComponent, config);
  }

  getDeadlineDisplay(value: any) {
    if (value === null || value === undefined) {
      return null;
    }

    const numValue = Number(value);
    const option = SCREENING_INFO_REQUEST_DEADLINE_OPTIONS.find(o => o.value === numValue);

    if (!option || option.value === null) {
      return null;
    }

    const isManual = numValue === 999;
    return {
      isManual: isManual,
      label: option.label,
      subText: isManual ? "確定時の指定も可能です" : "日程確定時に日付が決まります"
    };
  }

  close(param?: any) { this.dialog.close(param); }

  cancelMailTemplate() {
    if (this.mailTemplateSubscription) this.mailTemplateSubscription.unsubscribe();
    this.inConfirm = false;
    this.isTemplateLoadInProgress = false;
  }

  cancelSend() { if (this.sendSubscription) this.sendSubscription.unsubscribe(); }

  isDateInvalid(i: number): boolean {
    if (!this.screeningInfo) return false;
    const isChecked = this.model["request" + i];
    const deadline = this.screeningInfo["request" + i + "Deadline"];
    const isManual = deadline && Number(deadline) === 999;

    return !!(isChecked && isManual && !this.model["request" + i + "DeadlineDate"]);
  }

  isValidForConfirm(): boolean {
    if (!(this.model && this.model.toEmails && this.model.toEmails.length)) {
      return false;
    }

    if (this.screeningInfo) {
      for (let i = 1; i <= 3; i++) {
        if (this.isDateInvalid(i)) {
          return false;
        }
      }
    }
    return true;
  }
}
